import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import ChatSend from "../../../assets/img/chat-send.svg";
import ChatSendStop from "../../../assets/img/chat-send-stop.svg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDown } from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import ChatHeader from "./ChatHeader";
import MessageList from "./MessageList";
import ChatInput from "./ChatInput";
import ContinueButton from "../../Atoms/ContinueButton";
import { generateAndDownloadDocx } from "../../../utils/docxUtils";
import Button from "../../Atoms/Button";
import TranslatorService from "../../../services/Translator/TranslatorService";

const Chat = ({
  OnLikeButtonPress,
  onSendMessage,
  messages,
  isInitialized,
  setEnabledTextArea,
  enabledTextArea,
  showFirstMessageSkeleton,
  isMessageIncomplete,
  handleSendContinueMessage,
  projectId,
  overlay,
  diff,
  chatId,
}) => {
  // Estados
  const [textareaHeight, setTextareaHeight] = useState("20");
  const [iconSendButton, setIconSendButton] = useState(ChatSend);
  const [chatStarts, setChatStarts] = useState(false);
  const [showPreferences, setShowPreferences] = useState(true);
  const [message, setMessage] = useState("");
  const [systemContextMessage, setSystemContextMessage] = useState("");
  const [descricao, setDescricao] = useState(null);
  const [overlayMessage, setOverlayMessage] = useState("");
  const [userCodePairs, setUserCodePairs] = useState([]);
  const [showDetails, setShowDetails] = useState(false);

  const [enableRoll, setEnableRoll] = useState(true);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const chatContainerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const scrollButtonRef = useRef(null);
  const enableRollRef = useRef(enableRoll);
  const [oldChatId, setOldChatId] = useState(null);

  const { t } = useTranslation();

  const location = useLocation();
  const isGeral = location.state?.isGeral;
  const stateProjectName = location.state?.projectName;
  const fromHomePrompt = location.state?.prompt;
  // Atualiza enableRollRef quando enableRoll muda
  useEffect(() => {
    enableRollRef.current = enableRoll;
  }, [enableRoll]);

  const toggleDetails = () => setShowDetails(!showDetails);

  const resetChat = () => {
    setDescricao(null);
    setSystemContextMessage("");
    setOverlayMessage("");
    setChatStarts(false);
  };

  const isSystemMessage = (message) => {
    return (
      message.startsWith("<system-context>") &&
      message.endsWith("</system-context>")
    );
  };

  const groupMessages = (messages) => {
    const grouped = [];
    let prevMsg = null;

    messages.forEach((msg) => {
      if (prevMsg && prevMsg.role === "assistant" && msg.role === "assistant") {
        prevMsg.content += "\n" + msg.content;
      } else {
        const newMsg = { ...msg };
        grouped.push(newMsg);
        prevMsg = newMsg;
      }
    });
    return grouped;
  };

  const groupedMessages = groupMessages(messages);

  const toFirstLetterUpperCase = (string) => {
    return string
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join("");
  };

  useEffect(() => {
    setChatStarts(isInitialized);
  }, [isInitialized]);

  const setChatContainerRef = useCallback((node) => {
    chatContainerRef.current = node;
  }, []);

  useEffect(() => {
    if (messages.length > 0) {
      setChatStarts(true);
    }
  }, [messages]);

  useEffect(() => {
    const newCodePairs = [];
    let lastUserCode = null;

    messages.forEach((msg, index) => {
      if (msg.role === "user" && msg.content.includes("```")) {
        const codes = msg.content.match(/```(.*?)```/gs);
        if (codes) {
          const longestCode = codes.reduce(
            (longest, current) =>
              current.length > longest.length ? current : longest,
            ""
          );
          const codeContent = longestCode.replace(/```/g, "");
          lastUserCode = { index, codeContent };
        }
      } else if (
        msg.role === "assistant" &&
        msg.content.includes("```") &&
        lastUserCode !== null
      ) {
        const codes = msg.content.match(/```(.*?)```/gs);
        if (codes) {
          const longestCode = codes.reduce(
            (longest, current) =>
              current.length > longest.length ? current : longest,
            ""
          );
          const codeContent = longestCode.replace(/```/g, "");
          newCodePairs.push({
            userIndex: lastUserCode.index,
            assistantIndex: index,
            userCode: lastUserCode.codeContent,
            assistantCode: codeContent,
          });
          lastUserCode = null;
        }
      }
    });

    setUserCodePairs(newCodePairs);
  }, [messages]);

  const scrollToBottom = () => {
    const container = document.getElementById("chat-container-app");
    if (container) {
      container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
    }
  };

  useEffect(() => {
    if (enableRoll) {
      scrollToBottom();
    }
  }, [messages, enableRoll, chatStarts]);

  const handleWheel = (e) => {
    const { deltaY } = e;
    const container = chatContainerRef.current;
    if (container) {
      const { scrollTop, scrollHeight, clientHeight } = container;

      if (deltaY < 0) {
        if (scrollTop > 0 && enableRollRef.current) {
          setEnableRoll(false);
          setShowScrollButton(true);
        }
      } else if (deltaY > 0) {
        const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;
        if (isAtBottom && !enableRollRef.current) {
          setEnableRoll(true);
          setShowScrollButton(false);
        }
      }
    }
  };

  useEffect(() => {
    if (
      !showPreferences &&
      !chatStarts &&
      messages.length === 0 &&
      overlay &&
      overlayMessage
    ) {
      setEnabledTextArea(false);
      setMessage(overlayMessage);
      setTimeout(function () {
        const buttonSender = document.getElementById("button-sender");
        if (buttonSender) {
          buttonSender.click();
        }
      }, 200);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPreferences, chatStarts, messages, overlay, overlayMessage]);

  useEffect(() => {
    if (oldChatId !== null && oldChatId !== undefined) {
      resetChat();
    }
    setOldChatId(chatId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId]);

  useEffect(() => {
    resetChat();
  }, [projectId]);

  useEffect(() => {
    if (overlay) {
      setShowPreferences(true);
    } else {
      setShowPreferences(false);
    }
  }, [overlay]);

  useEffect(() => {
    if(isGeral && fromHomePrompt){
      setMessage(fromHomePrompt);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[])

  useEffect(() => {
    if(message && isGeral){
      handleSendMessage();
      changeIcon();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message])

  const handleInput = (event) => {
    const textarea = event.target;
    textarea.style.height = "20px";
    let newHeight = textarea.scrollHeight;
    const minHeight = 20;
    const maxHeight = 200;
    if (newHeight < minHeight) {
      newHeight = minHeight;
    } else if (newHeight > maxHeight) {
      newHeight = maxHeight;
      textarea.style.overflowY = "auto";
    } else {
      textarea.style.overflowY = "hidden";
    }
    textarea.style.height = `${newHeight}px`;
    setTextareaHeight(`${newHeight}px`);
    setMessage(textarea.value);
    checkIfUserCanSend(textarea.value);
  };

  const sendDebounceTimerRef = useRef(null);
  const [isSending, setIsSending] = useState(false);
  const SEND_DEBOUNCE_MS = 400;

  useEffect(() => {
    return () => {
      if (sendDebounceTimerRef.current) {
        clearTimeout(sendDebounceTimerRef.current);
      }
    };
  }, []);

  const inputDisabled = isSending || !enabledTextArea;

  const [thinkingMessage, setThinkingMessage] = useState("");
  useEffect(() => {
    if (!inputDisabled) {
      setThinkingMessage("");
      return;
    }
    const pool = t("chatThinking.messages", { returnObjects: true }) || [];
    if (!Array.isArray(pool) || pool.length === 0) return;
    const pick = () => pool[Math.floor(Math.random() * pool.length)];
    setThinkingMessage(pick());
    const id = setInterval(() => setThinkingMessage(pick()), 1500);
    return () => clearInterval(id);
  }, [inputDisabled, t]);

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      if (inputDisabled) return;
      handleSendMessage();
    }
  };

  const checkIfUserCanSend = (text) => {
    const messageTrimmed = text.trim();
    const buttonSender = document.getElementById("button-sender");

    if (messageTrimmed.length > 0) {
      if (buttonSender) {
        buttonSender.style.backgroundColor = "#000000";
        buttonSender.style.cursor = "pointer";
        buttonSender.disabled = false;
      }
    } else {
      if (buttonSender) {
        buttonSender.style.backgroundColor = "#D7D7D7";
        buttonSender.style.cursor = "default";
        buttonSender.disabled = true;
      }
    }
  };

  const handleSendMessage = useCallback(() => {
    if (!message) {
      Swal.fire({
        title: "Digite algo antes de enviar ao assistente!",
        customClass: {
          popup: 'custom-popup',
          title: 'custom-title',
          htmlContainer: 'custom-content',
          confirmButton: 'custom-confirm'
        }
      });
      return;
    }

    if (sendDebounceTimerRef.current) {
      clearTimeout(sendDebounceTimerRef.current);
    }

    setIsSending(true);
    sendDebounceTimerRef.current = setTimeout(() => {
      sendDebounceTimerRef.current = null;
      setEnabledTextArea(false);

      if (message.trim()) {
        let messageContent = "";
        onSendMessage(messageContent + message, systemContextMessage);
        setMessage("");
        setTextareaHeight("20");
      }
      setIsSending(false);
    }, SEND_DEBOUNCE_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message, onSendMessage, systemContextMessage]);

  const changeIcon = () => {
    setChatStarts(true);
    setIconSendButton((prevIcon) =>
      prevIcon === ChatSend ? ChatSendStop : ChatSend
    );
  };

  /**
   * Função para gerar e baixar o DOCX a partir do conteúdo Markdown.
   * @param {string} msgContent - Conteúdo Markdown a ser convertido.
   */
  const handleDownloadDocx = async (msgContent) => {
    try {
      if (!msgContent || msgContent.trim() === "") {
        Swal.fire("Nenhum conteúdo Markdown para converter.");
        return;
      }

      // Gera e baixa o DOCX a partir do Markdown
      generateAndDownloadDocx(msgContent);
    } catch (error) {
      console.error("Erro ao gerar o arquivo DOCX:", error);
      Swal.fire("Erro ao gerar o documento. Por favor, tente novamente.");
    }
  };

  return (
    <div className="main d-flex flex-column vh-100 overflow-hidden">
      {showPreferences &&
        !chatStarts &&
        messages.length === 0 &&
        React.isValidElement(overlay) && (
          <div>
            {React.cloneElement(overlay, {
              savePreferences: () => setShowPreferences(false),
              setOverlayMessage,
              setDescricao,
              setSystemContextMessage,
            })}
          </div>
        )}
      {!(showPreferences && !chatStarts && messages.length === 0) && (
        <div className="chat-content d-flex flex-column flex-grow-1 overflow-hidden position-relative" style={{ minHeight: 0 }}>
          {/* Cabeçalho do Chat */}
          <ChatHeader
            name="Chat"
            projectId={projectId}
            projectName={stateProjectName || ""}
            descricao={descricao}
            toggleDetails={toggleDetails}
            showDetails={showDetails}
            toFirstLetterUpperCase={toFirstLetterUpperCase}
          />

          {/* Contêiner de Mensagens */}
          <div
            id="chat-container-app"
            className="flex-grow-1 overflow-auto"
            style={{ minHeight: 0 }}
          >
            {chatStarts && (
              <MessageList
                groupedMessages={groupedMessages}
                userCodePairs={userCodePairs}
                isSystemMessage={isSystemMessage}
                diff={diff}
                handleDownloadDocx={handleDownloadDocx}
                messagesEndRef={messagesEndRef}
                containerRef={setChatContainerRef}
                showFirstMessageSkeleton={showFirstMessageSkeleton}
                OnLikeButtonPress={OnLikeButtonPress}
                handleWheel={handleWheel}
              />
            )}
            {isMessageIncomplete ? (
              <ContinueButton onClick={handleSendContinueMessage}>
                <TranslatorService path="chatForms.continueButton" />
              </ContinueButton>
            ) : null}
          </div>

          {showScrollButton && (
            <div className="position-relative d-flex justify-content-center p-2">
              <Button
                ref={scrollButtonRef}
                className="btn btn-primary rounded-circle shadow"
                onClick={() => {
                  setEnableRoll(true);
                  scrollToBottom();
                  setShowScrollButton(false);
                }}
                aria-label="Rolagem para o final"
              >
                <FontAwesomeIcon icon={faArrowDown} />
              </Button>
            </div>
          )}

          {/* Entrada de Texto */}
          <ChatInput
            textareaHeight={textareaHeight}
            handleInput={handleInput}
            handleKeyDown={handleKeyDown}
            message={message}
            handleSendMessage={handleSendMessage}
            changeIcon={changeIcon}
            iconSendButton={iconSendButton}
            t={t}
            isSending={inputDisabled}
            thinkingMessage={thinkingMessage}
          />
        </div>
      )}
    </div>
  );
};

export default Chat;
